import React from 'react';
import { QuestionFolder } from '@/types';

interface FolderCardProps {
  folder: QuestionFolder;
  questionSetCount: number;
  subFolderCount: number;
  onOpen: (folderId: string) => void;
  onRename?: (folder: QuestionFolder) => void;
  onMove?: (folder: QuestionFolder) => void;
  onDelete?: (folder: QuestionFolder) => void;
}

export const FolderCard: React.FC<FolderCardProps> = ({
  folder,
  questionSetCount,
  subFolderCount,
  onOpen,
  onRename,
  onMove,
  onDelete,
}) => {
  const accentColor = folder.color || '#f59e0b';

  const handleOpen = () => {
    onOpen(folder.id);
  };

  return (
    <div
      style={{
        flex: '0 1 280px',
        minWidth: '240px',
        maxWidth: '300px',
        minHeight: '188px',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, ${accentColor}14 180%)`,
        backdropFilter: 'blur(20px)',
        borderRadius: '18px',
        padding: '1.1rem',
        boxShadow: `0 10px 28px ${accentColor}18`,
        border: `1px solid ${accentColor}35`,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
      }}
      className="group"
      onClick={handleOpen}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 14px 34px ${accentColor}24`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = `0 10px 28px ${accentColor}18`;
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <span
            style={{
              padding: '0.3rem 0.65rem',
              borderRadius: '999px',
              background: `${accentColor}1f`,
              color: accentColor,
              fontSize: '0.72rem',
              fontWeight: '700',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              border: `1px solid ${accentColor}35`,
            }}
          >
            Folder
          </span>
        </div>

        {(onRename || onMove || onDelete) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginLeft: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: 'rgba(255, 255, 255, 0.86)',
                padding: '0.3rem',
                borderRadius: '10px',
                boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
                border: '1px solid rgba(226, 232, 240, 0.9)',
              }}
            >
              {onRename && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(folder);
                  }}
                  style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    color: '#f59e0b',
                    cursor: 'pointer',
                    padding: '0.35rem 0.45rem',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      'rgba(245, 158, 11, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      'rgba(245, 158, 11, 0.1)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Rename folder"
                >
                  ✏️
                </button>
              )}
              {onMove && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMove(folder);
                  }}
                  style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    padding: '0.35rem 0.45rem',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      'rgba(59, 130, 246, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      'rgba(59, 130, 246, 0.1)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Move folder"
                >
                  📦
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(folder);
                  }}
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '0.35rem 0.45rem',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Delete folder"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.9rem',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            width: '54px',
            height: '54px',
            borderRadius: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.9rem',
            background: `${accentColor}20`,
            border: `1px solid ${accentColor}35`,
            boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.75), 0 8px 16px ${accentColor}16`,
            flexShrink: 0,
          }}
        >
          {folder.icon || '📁'}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <h3
            style={{
              margin: '0 0 0.35rem 0',
              fontSize: '1.05rem',
              fontWeight: '700',
              color: '#1f2937',
              lineHeight: '1.25',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={folder.name}
          >
            {folder.name}
          </h3>

          <p
            style={{
              margin: 0,
              fontSize: '0.82rem',
              color: '#64748b',
              lineHeight: '1.45',
              display: '-webkit-box',
              WebkitLineClamp: folder.description ? 2 : 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={folder.description || 'Open folder'}
          >
            {folder.description ||
              'Open this folder to browse its sets and nested folders.'}
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.35rem 0.6rem',
            borderRadius: '999px',
            background: 'rgba(255, 255, 255, 0.78)',
            border: '1px solid rgba(148, 163, 184, 0.24)',
            color: '#475569',
            fontSize: '0.78rem',
            fontWeight: '600',
          }}
        >
          <span>📄</span>
          <span>
            {questionSetCount} {questionSetCount === 1 ? 'set' : 'sets'}
          </span>
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.35rem 0.6rem',
            borderRadius: '999px',
            background: 'rgba(255, 255, 255, 0.78)',
            border: '1px solid rgba(148, 163, 184, 0.24)',
            color: '#475569',
            fontSize: '0.78rem',
            fontWeight: '600',
          }}
        >
          <span>📁</span>
          <span>
            {subFolderCount} {subFolderCount === 1 ? 'folder' : 'folders'}
          </span>
        </div>
      </div>

      <div
        style={{
          marginTop: 'auto',
          paddingTop: '0.85rem',
          borderTop: `1px solid ${accentColor}24`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#374151',
        }}
      >
        <span
          style={{
            fontSize: '0.86rem',
            fontWeight: '700',
          }}
        >
          Open folder
        </span>
        <div
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `${accentColor}20`,
            border: `1px solid ${accentColor}35`,
            color: accentColor,
            transform: 'translateX(0)',
            transition: 'transform 0.2s ease',
          }}
          className="group-hover:translate-x-1"
        >
          →
        </div>
      </div>
    </div>
  );
};
